import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableRow,
  TableCell,
  TableHead,
  TableBody,
  Button,
  IconButton,
  CircularProgress,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import { Delete, Edit } from "@mui/icons-material";
import { useAuth } from "../context/AuthContext";
import axios from "axios";
import { useLocation, useNavigate, useOutletContext } from "react-router-dom";

const WaterBalanceList = () => {
  const { accessToken, isAdmin } = useAuth();
  const baseURL = process.env.REACT_APP_BASEURL;
  const navigate = useNavigate();
  const location = useLocation();
  const { userData } = useOutletContext();
  const selectedSiteId = location.state?.siteId;

  const [records, setRecords] = useState([]);
  const [siteNames, setSiteNames] = useState({});
  const [loading, setLoading] = useState(true);
  const [feedback, setFeedback] = useState({
    open: false,
    type: "",
    message: "",
  });
  const [deleteId, setDeleteId] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  useEffect(() => {
    if (!accessToken || !userData?.sites) return;

    const fetchRecords = async () => {
      setLoading(true);
      try {
        const hasSelectedSite = userData.sites.some(
          (siteId) => String(siteId) === String(selectedSiteId)
        );

        const [res, sitesRes] = await Promise.all([
          axios.get(`${baseURL}/water-balance/all`, {
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
            params: {
              site_id: hasSelectedSite ? selectedSiteId : "all",
              user_sites: userData.sites.join(","),
            },
          }),
          axios.get(`${baseURL}/get-sites`, {
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
          }),
        ]);

        const allowedSiteIds = new Set(userData.sites.map((siteId) => String(siteId)));
        const namesById = (sitesRes.data?.data || []).reduce((acc, site) => {
          if (allowedSiteIds.has(String(site.id))) {
            acc[String(site.id)] = site.name || `Site ${site.id}`;
          }
          return acc;
        }, {});

        setRecords(res.data?.data || []);
        setSiteNames(namesById);
      } catch (error) {
        console.error("Error loading water balance records:", error);
        setFeedback({
          open: true,
          type: "error",
          message: "Failed to load water balance records",
        });
      } finally {
        setLoading(false);
      }
    };

    fetchRecords();
  }, [accessToken, baseURL, userData, selectedSiteId]);

  const handleDelete = async () => {
    try {
      await axios.delete(`${baseURL}/water-balance/${deleteId}`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });

      setFeedback({
        open: true,
        type: "success",
        message: "Record deleted successfully",
      });

      // Remove deleted row from UI
      setRecords((prev) => prev.filter((r) => r._id !== deleteId));
    } catch (error) {
      console.error("Delete error:", error);
      setFeedback({
        open: true,
        type: "error",
        message: "Failed to delete record",
      });
    } finally {
      setConfirmOpen(false);
      setDeleteId(null);
    }
  };

  return (
    <Box sx={{ maxWidth: "100%", mx: "auto", mt: 4 }}>
      <Card sx={{ p: 2 }}>
        <CardContent>
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
            <Box>
              <Typography variant="h5" fontWeight="bold">
                Water Balance Records
              </Typography>
              {selectedSiteId && (
                <Typography variant="body2" color="text.secondary">
                  Showing records for:{" "}
                  {siteNames[String(selectedSiteId)] || `Site ${selectedSiteId}`}
                </Typography>
              )}
            </Box>
            {isAdmin && (
              <Button
                variant="contained"
                onClick={() => navigate("/water-balance/add")}
              >
                + Add New
              </Button>
            )}
          </Box>

          {loading ? (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          ) : records.length === 0 ? (
            <Typography>No water balance records found.</Typography>
          ) : (
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>
                    <strong>Site ID</strong>
                  </TableCell>
                  <TableCell>
                    <strong>Site Name</strong>
                  </TableCell>
                  <TableCell>
                    <strong>Month</strong>
                  </TableCell>
                  <TableCell>
                    <strong>Water Saved</strong>
                  </TableCell>
                  <TableCell>
                    <strong>Unit</strong>
                  </TableCell>
                  {isAdmin && (
                    <TableCell>
                      <strong>Actions</strong>
                    </TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {records.map((rec) => (
                  <TableRow key={rec._id}>
                    <TableCell>{rec.site_id}</TableCell>
                    <TableCell>
                      {siteNames[String(rec.site_id)] || `Site ${rec.site_id}`}
                    </TableCell>
                    <TableCell>{rec.month}</TableCell>
                    <TableCell>{rec.water_balance}</TableCell>
                    <TableCell>{rec.unit || "L"}</TableCell>
                    {isAdmin && (
                      <TableCell>
                        <IconButton
                          color="primary"
                          onClick={() =>
                            navigate(`/water-balance/edit/${rec._id}`)
                          }
                        >
                          <Edit />
                        </IconButton>
                        <IconButton
                          color="error"
                          onClick={() => {
                            setDeleteId(rec._id);
                            setConfirmOpen(true);
                          }}
                        >
                          <Delete />
                        </IconButton>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Feedback Snackbar */}
      <Snackbar
        open={feedback.open}
        autoHideDuration={3000}
        onClose={() => setFeedback({ ...feedback, open: false })}
      >
        <Alert severity={feedback.type}>{feedback.message}</Alert>
      </Snackbar>

      {/* Confirmation Dialog */}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Delete Record?</DialogTitle>

        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this water balance record? This
            action cannot be undone.
          </DialogContentText>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button color="error" onClick={handleDelete}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default WaterBalanceList;
